/**
 * Utility functions for formatting dates and times
 */

/**
 * Format time from HH:mm:ss to 12-hour format with AM/PM
 * @param {string} time - Time in HH:mm:ss or HH:mm format
 * @returns {string} - Time in 12-hour format (e.g., "2:30 PM")
 */
export const formatTime12Hour = (time) => {
  if (!time) return '';
  
  // Handle both HH:mm:ss and HH:mm formats
  const parts = time.split(':');
  let hours = parseInt(parts[0], 10);
  const minutes = parts[1];
  
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12; // Convert 0 to 12 for midnight
  
  return `${hours}:${minutes} ${ampm}`;
};

/**
 * Format date to "dd MMM yyyy" format
 * @param {string|Date} date - Date string or Date object
 * @returns {string} - Formatted date (e.g., "24 Nov 2025")
 */
export const formatDate = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Check if valid date
  if (isNaN(dateObj.getTime())) return date;
  
  const day = dateObj.getDate();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[dateObj.getMonth()];
  const year = dateObj.getFullYear();
  
  return `${day} ${month} ${year}`;
};

/**
 * Format date with day name "dd MMM yyyy (Day)"
 * @param {string|Date} date - Date string or Date object
 * @returns {string} - Formatted date with day (e.g., "24 Nov 2025 (Sunday)")
 */
export const formatDateWithDay = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Check if valid date
  if (isNaN(dateObj.getTime())) return date;
  
  const day = dateObj.getDate();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  const month = monthNames[dateObj.getMonth()];
  const year = dateObj.getFullYear();
  const dayName = dayNames[dateObj.getDay()];
  
  return `${day} ${month} ${year} (${dayName})`;
};


/**
 * Format time range to 12-hour format
 * @param {string} startTime - Start time in HH:mm:ss format
 * @param {string} endTime - End time in HH:mm:ss format
 * @returns {string} - Formatted time range (e.g., "9:00 AM - 11:00 AM")
 */
export const formatTimeRange = (startTime, endTime) => {
  if (!startTime || !endTime) return '';
  return `${formatTime12Hour(startTime)} - ${formatTime12Hour(endTime)}`;
};
